import React, { useState } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import JumboConfirmDialog from "@jumbo/components/JumboConfirmDialog";
import JumboIconButton from "@jumbo/components/JumboIconButton";
import { LoadingButton } from "@mui/lab";
import Div from "@jumbo/shared/Div";
import { useNavigate, useLocation } from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { canAccessRunAndSubmit } from "app/utils/permissions";
import { ROUTES } from "app/utils/constants/routeConstants";

const EditorControls = ({
  selectedLanguage = null,
  onLanguageChange,
  onRun,
  onSubmit,
  onReset,
  isRunning = false,
  isSubmitting = false,
  hasActiveTemplates = true,
  metadataLoading = false,
  metadataError = null,
  availableLanguages = [],
  codeConstants = {},
  isActive,
  onActiveChange,
  setOutputTabValue,
  setCodeTabValue,
  codeTabValue,
  problemData,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  const [openUpgradeDialog, setOpenUpgradeDialog] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { authUser } = useJumboAuth();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleOpenDialog = () => {
    if (onReset) setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirmReset = () => {
    if (onReset) onReset();
    setOpenDialog(false);
    if (setOutputTabValue) setOutputTabValue(0);
    if (setCodeTabValue) setCodeTabValue(0);
  };

  const handleLoginDialogOpen = () => {
    setOpenLoginDialog(true);
  };

  const handleLoginDialogClose = () => {
    setOpenLoginDialog(false);
  };

  const handleUpgradeDialogOpen = () => {
    setOpenUpgradeDialog(true);
  };

  const handleUpgradeDialogClose = () => {
    setOpenUpgradeDialog(false);
  };

  const handleLoginConfirm = () => {
    const redirectURI = encodeURIComponent(location.pathname);
    navigate(`${ROUTES.USER_LOGIN}?redirectURI=${redirectURI}`);
  };

  const handleUpgradeConfirm = () => {
    navigate(ROUTES.UPGRADE);
  };

  const handleRunClick = () => {
    if (!isLoggedIn) {
      handleLoginDialogOpen();
    } else if (problemData?.forPaidOnly === true && !canAccessButtons) {
      handleUpgradeDialogOpen();
    } else if (onRun) {
      onRun();
      if (setOutputTabValue) setOutputTabValue(1);
    }
  };

  const handleSubmitClick = () => {
    if (!isLoggedIn) {
      handleLoginDialogOpen();
    } else if (problemData?.forPaidOnly === true && !canAccessButtons) {
      handleUpgradeDialogOpen();
    } else if (onSubmit) {
      onSubmit();
      if (setCodeTabValue) setCodeTabValue(1);
      if (setOutputTabValue) setOutputTabValue(1);
    }
  };

  const handleLanguageChange = (event) => {
    if (onLanguageChange) {
      onLanguageChange(event);
      if (setCodeTabValue && codeTabValue === 1) {
        setCodeTabValue(0);
      }
    }
  };

  // Determine if controls should be enabled
  const controlsEnabled = !!selectedLanguage || hasActiveTemplates;

  // Authentication and role checks
  const isLoggedIn = !!authUser;
  const canAccessButtons = canAccessRunAndSubmit(authUser, {
    requireLogin: true,
  });

  return (
    <>
      <Div
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          zIndex: 10000,
          position: "relative",
          overflow: "visible",
        }}
      >
        {isActive !== undefined && onActiveChange && (
          <FormControlLabel
            control={
              <Checkbox
                name="active"
                checked={isActive}
                onChange={onActiveChange}
                disabled={metadataLoading || metadataError || !selectedLanguage}
              />
            }
            label="Active"
          />
        )}

        {onLanguageChange && (
          <FormControl sx={{ maxWidth: isMobile ? 80 : 120 }}>
            <InputLabel
              id="code-language-select-label"
              shrink={!!selectedLanguage}
            >
              Language
            </InputLabel>
            <Select
              labelId="code-language-select-label"
              id="code-language-select"
              value={selectedLanguage || ""}
              label="Language"
              onChange={handleLanguageChange}
              size="small"
              disabled={metadataLoading || metadataError}
            >
              {metadataLoading ? (
                <MenuItem value="" disabled>
                  Loading languages...
                </MenuItem>
              ) : metadataError ? (
                <MenuItem value="" disabled>
                  Error loading languages
                </MenuItem>
              ) : availableLanguages.length > 0 ? (
                availableLanguages.map((lang) => (
                  <MenuItem key={lang.key} value={lang}>
                    {codeConstants[lang.key]?.name || lang.value || lang.key}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No active languages available
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}

        {onRun && (
          <Box
            sx={{
              display: "inline-block",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
              pointerEvents: "auto",
              zIndex: 10000,
              cursor: "pointer",
            }}
          >
            <LoadingButton
              loading={isRunning}
              loadingPosition="start"
              startIcon={<PlayArrowIcon />}
              variant="contained"
              color="primary"
              onClick={handleRunClick}
              disabled={
                isRunning ||
                isSubmitting ||
                !controlsEnabled ||
                metadataLoading ||
                metadataError
              }
              size="small"
              aria-label="run"
            >
              Run
            </LoadingButton>
          </Box>
        )}

        {onSubmit && (
          <Box
            sx={{
              display: "inline-block",
              "&:hover": {
                opacity: 1,
                borderRadius: 2,
              },
              pointerEvents: "auto",
              zIndex: 10000,
              cursor: "pointer",
            }}
          >
            <JumboIconButton
              onClick={handleSubmitClick}
              tooltip="Submit"
              aria-label="submit"
              disabled={
                isSubmitting ||
                isRunning ||
                !controlsEnabled ||
                metadataLoading ||
                metadataError
              }
              sx={{
                "&:hover": { opacity: 1, borderRadius: 2 },
                color: "#28c244",
                borderColor: "#28c244",
              }}
            >
              <BackupOutlinedIcon />
            </JumboIconButton>
          </Box>
        )}

        {onReset && (
          <JumboIconButton
            onClick={handleOpenDialog}
            color="primary"
            tooltip="Reset code to default template"
            aria-label="reset"
            disabled={!controlsEnabled || metadataLoading || metadataError}
            sx={{
              "&:hover": { opacity: 1, borderRadius: 2 },
            }}
          >
            <SettingsBackupRestoreIcon />
          </JumboIconButton>
        )}
      </Div>

      <JumboConfirmDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmReset}
        title="Are you sure?"
        message="Resetting will replace your current code with the default template."
        cancelText="Cancel"
        confirmText="Confirm"
        cancelColor="error"
        confirmColor="success"
        confirmVariant="contained"
      />

      <JumboConfirmDialog
        open={openLoginDialog}
        onClose={handleLoginDialogClose}
        onConfirm={handleLoginConfirm}
        title="Login Required"
        message="Please login to run or submit the code."
        cancelText="Cancel"
        confirmText="Login"
        cancelColor="error"
        confirmColor="primary"
        confirmVariant="contained"
      />

      <JumboConfirmDialog
        open={openUpgradeDialog}
        onClose={handleUpgradeDialogClose}
        onConfirm={handleUpgradeConfirm}
        title="Upgrade Required"
        message="Upgrade to a premium account to run or submit the code."
        cancelText="Cancel"
        confirmText="Upgrade"
        cancelColor="error"
        confirmColor="primary"
        confirmVariant="contained"
      />
    </>
  );
};

export default EditorControls;
